import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import OnboardingLayout from '../../components/layouts/onboarding/OnboardingLayout';
import storage from '../../utils/storage';
import {
  FormContainer,
  ButtonContainer,
  CompanyDisplay,
  CompanyValue
} from '../../styles/components';
import PrimaryButton from '../../components/common/PrimaryButton';
import { colors, typography } from '../../styles/theme/index';

const CoverContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 24px 0;
`;

const CoverBox = styled.div`
  background: ${props => props.selected ? colors.accent + '30' : colors.background.light};
  border: 1px solid ${props => props.selected ? colors.accent : colors.border};
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  &:hover {
    background: ${colors.accent}20;
    border-color: ${colors.accent};
  }
`;

const CoverContent = styled.div`
  flex: 1;
`;

const CoverTitle = styled.h3`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.lg};
  color: ${colors.text.primary};
  margin: 0 0 8px;
  font-weight: ${typography.fontWeight.bold};
`;

const CoverDescription = styled.p`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  color: ${colors.text.secondary};
  margin: 0;
  line-height: 1.5;
`;

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" 
      fill={colors.primary}
    />
  </svg>
);

const CheckIconWrapper = styled.div`
  opacity: ${props => props.visible ? 1 : 0};
  transform: ${props => props.visible ? 'scale(1)' : 'scale(0.8)'};
  transition: all 0.2s ease;
  margin-left: 16px;
  flex-shrink: 0;
`;

const OnboardingStep6 = () => {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [selectedCovers, setSelectedCovers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for required data from previous steps
    const step5Data = storage.getItem('step5Data');
    const step3Data = storage.getItem('step3Data');
    const pipedriveData = storage.getPipedriveData();
    
    if (!step5Data || !pipedriveData?.organizationId) {
      // If missing step 5 data, go back to step 5
      // If missing Pipedrive data, go back to step 1
      navigate(step5Data ? '/onboarding/step-one' : '/onboarding/step-five');
      return;
    }

    // Load company name from step 3
    if (step3Data) {
      const { companyName } = JSON.parse(step3Data);
      setCompanyName(companyName);
    }

    // Load previously saved covers if they exist
    const savedCovers = storage.getItem('step6Data');
    if (savedCovers) {
      const { selectedCovers } = JSON.parse(savedCovers);
      setSelectedCovers(selectedCovers);
    }
  }, [navigate]);

  const coverOptions = [
    {
      id: 'Public Liability',
      title: 'Public Liability',
      description: 'Insurance that covers your business against claims made by members of the public'
    },
    {
      id: 'Employer Liability',
      title: 'Employer Liability',
      description: 'Mandatory insurance if your business employs staff, protecting against employee claims'
    },
    {
      id: 'Professional Indemnity',
      title: 'Professional Indemnity',
      description: 'Protects your business against claims of negligence or mistakes in your professional services'
    },
    {
      id: 'Directors and Officers',
      title: 'Directors and Officers',
      description: 'Protection for company directors and officers against claims of mismanagement'
    },
    {
      id: 'Cyber',
      title: 'Cyber',
      description: 'Protection against cyber attacks, data breaches, and other digital risks'
    },
    {
      id: 'Property / Landlord',
      title: 'Property / Landlord',
      description: 'Coverage for property damage and landlord-specific liabilities'
    }
  ];

  const toggleCover = (coverId) => {
    const newSelectedCovers = selectedCovers.includes(coverId)
      ? selectedCovers.filter(id => id !== coverId)
      : [...selectedCovers, coverId];
    
    setSelectedCovers(newSelectedCovers);
    // Store selected covers as user makes selections
    storage.setItem('step6Data', JSON.stringify({ selectedCovers: newSelectedCovers }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedCovers.length === 0) {
      setError('Please select at least one type of cover');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const pipedriveData = storage.getPipedriveData();
      if (!pipedriveData?.organizationId) {
        throw new Error('Organization ID not found');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/onboarding/step-six`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          selectedCovers: selectedCovers,
          organizationId: pipedriveData.organizationId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to update organization cover options');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to update organization cover options');
      }

      // Store selected covers before navigating
      storage.setItem('step6Data', JSON.stringify({ selectedCovers }));

      // Preserve any existing step 7 data
      const step7Data = storage.getItem('step7Data');
      if (!step7Data) {
        // Only initialize step 7 data if it doesn't exist
        storage.setItem('step7Data', JSON.stringify({
          coverLimits: {},
          properties: []
        }));
      }

      // Navigate to next step
      navigate('/onboarding/step-seven');
    } catch (error) {
      console.error('Form submission error:', error);
      setError(error.message || 'There was an error processing your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    // Store current selections before navigating back
    storage.setItem('step6Data', JSON.stringify({ selectedCovers }));
    
    // Don't clear step7 data when going back
    // This ensures property details are preserved when returning to step 7
    navigate('/onboarding/step-five');
  };

  return (
    <OnboardingLayout
      title="Select your"
      brandText="Insurance Covers"
      onBack={handleBack}
    >
      <FormContainer>
        <CompanyDisplay>
          <CompanyValue>{companyName}</CompanyValue>
        </CompanyDisplay>

        {error && (
          <div style={{ color: colors.error, marginBottom: '1rem' }}>
            {error}
          </div>
        )}
        <CoverContainer>
          {coverOptions.map((cover) => (
            <CoverBox
              key={cover.id}
              selected={selectedCovers.includes(cover.id)}
              onClick={() => toggleCover(cover.id)}
            >
              <CoverContent>
                <CoverTitle>{cover.title}</CoverTitle>
                <CoverDescription>{cover.description}</CoverDescription>
              </CoverContent>
              <CheckIconWrapper visible={selectedCovers.includes(cover.id)}>
                <CheckIcon />
              </CheckIconWrapper>
            </CoverBox>
          ))}
        </CoverContainer>
        <ButtonContainer>
          <PrimaryButton
            onClick={handleSubmit}
            disabled={isSubmitting || selectedCovers.length === 0}
          >
            {isSubmitting ? 'Processing...' : 'Continue'}
          </PrimaryButton>
        </ButtonContainer>
      </FormContainer>
    </OnboardingLayout>
  );
};

export default OnboardingStep6; 